import { useState, useEffect, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Spinner,
  Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell,
  Badge, Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  Field, Input, Select, Textarea, Tooltip,
  makeStyles, tokens, MessageBar, MessageBarBody,
} from '@fluentui/react-components'
import { Add24Regular, Edit24Regular, Delete24Regular } from '@fluentui/react-icons'
import { absenceRequestsService, AbsenceRequest, AbsenceRequestStatus, ABSENCE_TYPES, DayHalf, DAY_HALF_LABEL } from '../../services/absenceRequestsService'
import { meService } from '../../services/meService'
import { NativeDateInput } from '../../components/NativeDateInput'
import VacationBalanceSummary from '../../components/VacationBalanceSummary'
import { useAutoRetry } from '../../hooks/useAutoRetry'
import ErrorRetryBar from '../../components/ErrorRetryBar'

const STATUS_COLOR: Record<number, 'warning' | 'success' | 'danger' | 'subtle'> = {
  1: 'warning', 2: 'success', 3: 'danger', 4: 'subtle',
}
const STATUS_LABEL: Record<number, string> = {
  1: 'Schvalováno', 2: 'Schváleno', 3: 'Zamítnuto', 4: 'Stornováno',
}

function useIsMobile(breakpoint = 640) {
  return useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth <= breakpoint,
    () => false,
  )
}

const useStyles = makeStyles({
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  th: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3, textTransform: 'uppercase',
  },
  tableRow: {
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    cursor: 'default',
  },

  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fullRow: { gridColumn: '1 / -1' },
  hint: { fontSize: '11px', color: tokens.colorNeutralForeground3, marginTop: '2px' },
  actions: { display: 'flex', gap: '4px' },
})

const emptyForm = {
  absenceTypeId: '',
  startDate: '',
  endDate: '',
  location: '',
  note: '',
  totalDays: '',
  dayHalf: DayHalf.Full as DayHalf,
}

/** Přijme desetinnou čárku i tečku → číslo */
function parseDecimal(s: string): number {
  return Number(s.replace(',', '.'))
}

function parseApiError(err: any): string {
  const status = err?.response?.status
  if (status === 403) return 'Nemáte oprávnění k této operaci.'
  if (status === 409 && typeof err?.response?.data === 'string') return err.response.data
  if (err?.response?.data?.errors)
    return Object.values(err.response.data.errors).flat().join(', ')
  if (typeof err?.response?.data === 'string' && err.response.data.length < 1000)
    return err.response.data
  return 'Nastala chyba při ukládání.'
}

export default function MyRequestsPage() {
  const styles = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRequest, setEditingRequest] = useState<AbsenceRequest | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [totalDaysEdited, setTotalDaysEdited] = useState(false)
  const [formError, setFormError] = useState('')


  const { data: myProfile, isLoading: isProfileLoading } = useQuery({ queryKey: ['me'], queryFn: meService.getMe })
  const { data: requests, isLoading: isRequestsLoading, isError, refetch } = useQuery({
    queryKey: ['my-requests', myProfile?.id],
    queryFn: () => absenceRequestsService.getAll({ employeeId: myProfile?.id ?? undefined }),
    enabled: !!myProfile?.id,
  })
  const isLoading = isProfileLoading || isRequestsLoading
  const { autoRetryCount, autoRetryCountdown, maxRetries, resetAndRetry } = useAutoRetry(isError, refetch)
  const absenceTypes = ABSENCE_TYPES

  // Počet pracovních dnů ze serveru (respektuje DB svátky a rozvrh zaměstnance)
  const { data: serverDays } = useQuery({
    queryKey: ['working-days', form.startDate, form.endDate, myProfile?.id],
    queryFn: () => absenceRequestsService.getWorkingDays(form.startDate, form.endDate, myProfile!.id!),
    enabled: !!form.startDate && !!form.endDate && !!myProfile?.id && !totalDaysEdited,
    staleTime: 0,
  })

  useEffect(() => {
    if (serverDays != null && !totalDaysEdited) {
      setForm(f => ({ ...f, totalDays: serverDays > 0 ? String(serverDays).replace('.', ',') : '' }))
    }
  }, [serverDays, totalDaysEdited])

  const isSingleDay = !!form.startDate && !!form.endDate && form.startDate === form.endDate
  const isHalf = form.dayHalf !== DayHalf.Full

  // Vícedenní / prázdný rozsah → vždy celý den — #11
  useEffect(() => {
    if (!isSingleDay && form.dayHalf !== DayHalf.Full) {
      setForm(f => ({ ...f, dayHalf: DayHalf.Full }))
      setTotalDaysEdited(false)
    }
  }, [isSingleDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const setFullDay = () => { setTotalDaysEdited(true); setForm(f => ({ ...f, dayHalf: DayHalf.Full, totalDays: '1' })) }
  const setHalf = (dh: DayHalf) => { setTotalDaysEdited(true); setForm(f => ({ ...f, dayHalf: dh, totalDays: '0,5' })) }
  const adjustWholeDays = (delta: number) => {
    setTotalDaysEdited(true)
    setForm(f => ({ ...f, totalDays: String(Math.max(1, Math.round((parseDecimal(f.totalDays) || 0) + delta))) }))
  }

  const set = (field: keyof typeof emptyForm) => (e: any) =>
    setForm(f => ({ ...f, [field]: e.target?.value ?? e }))


  const openNewDialog = () => {
    setEditingRequest(null)
    const defaultTypeId = absenceTypes?.find(at => at.name === 'Dovolená')?.id
    setForm({ ...emptyForm, absenceTypeId: defaultTypeId ? String(defaultTypeId) : '' })
    setTotalDaysEdited(false)
    setFormError('')
    setDialogOpen(true)
  }

  const openEditDialog = (r: AbsenceRequest) => {
    setEditingRequest(r)
    setForm({
      absenceTypeId: String(r.absenceTypeId),
      startDate: r.startDate,
      endDate: r.endDate,
      location: r.location ?? '',
      note: r.note ?? '',
      totalDays: String(r.totalDays).replace('.', ','),
      dayHalf: r.dayHalf,
    })
    setTotalDaysEdited(true) // při editaci zachováme stávající počet dnů
    setFormError('')
    setDialogOpen(true)
  }

  const resetDialog = () => {
    setEditingRequest(null)
    setForm(emptyForm)
    setTotalDaysEdited(false)
    setFormError('')
    setDialogOpen(false)
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['my-requests', myProfile?.id] })

  const createMutation = useMutation({
    mutationFn: () => absenceRequestsService.create({
      employeeId: myProfile?.id ?? 0,
      absenceTypeId: Number(form.absenceTypeId),
      startDate: form.startDate,
      endDate: form.endDate,
      location: form.location,
      note: form.note || undefined,
      // Pokud uživatel dny ručně neupravil, necháme server přepočítat z DB svátků.
      totalDays: totalDaysEdited ? parseDecimal(form.totalDays) : undefined,
      dayHalf: form.dayHalf,
    }),
    onSuccess: () => { invalidate(); resetDialog() },
    onError: (err: any) => setFormError(parseApiError(err)),
  })

  const updateMutation = useMutation({
    mutationFn: () => absenceRequestsService.update(editingRequest!.id, {
      absenceTypeId: Number(form.absenceTypeId),
      startDate: form.startDate,
      endDate: form.endDate,
      location: form.location,
      note: form.note || undefined,
      totalDays: totalDaysEdited ? parseDecimal(form.totalDays) : undefined,
      dayHalf: form.dayHalf,
    }),
    onSuccess: () => { invalidate(); resetDialog() },
    onError: (err: any) => setFormError(parseApiError(err)),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: number) => absenceRequestsService.cancel(id),
    onSuccess: () => invalidate(),
    onError: () => alert('Stornování se nezdařilo.'),
  })

  const handleSubmit = () => {
    setFormError('')
    if (!form.absenceTypeId) { setFormError('Vyberte typ nepřítomnosti.'); return }
    if (!form.startDate)     { setFormError('Zadejte datum od.'); return }
    if (!form.endDate)       { setFormError('Zadejte datum do.'); return }
    if (form.endDate < form.startDate) { setFormError('Datum do nemůže být dříve než datum od.'); return }
    if (!form.location)      { setFormError('Zadejte místo pobytu.'); return }
    const days = parseDecimal(form.totalDays)
    if (!form.totalDays || isNaN(days) || days <= 0) { setFormError('Počet dnů musí být větší než 0.'); return }
    if (isHalf && !isSingleDay) { setFormError('Půl dne lze zadat jen na jeden den.'); return }
    if (!isHalf && days % 1 !== 0) { setFormError('Zlomkové vícedenní volno nelze — rozdělte na celé dny a půlden.'); return }
    editingRequest ? updateMutation.mutate() : createMutation.mutate()
  }

  const handleCancel = (r: AbsenceRequest) => {
    if (!confirm(`Opravdu chcete stornovat žádost o ${r.absenceTypeName} (${r.startDate} – ${r.endDate})?`)) return
    cancelMutation.mutate(r.id)
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const isMobile = useIsMobile()

  return (
    <div>
      <div className={styles.header}>
        <Text size={600} weight="semibold">{t('absences.myRequests')}</Text>
        <Button appearance="primary" icon={<Add24Regular />}
          onClick={() => isMobile ? navigate('/requests/new') : openNewDialog()}>
          {t('absences.newRequest')}
        </Button>
      </div>

      {myProfile && <VacationBalanceSummary />}

      <ErrorRetryBar
        isError={isError}
        message="Nepodařilo se načíst žádosti."
        autoRetryCount={autoRetryCount}
        autoRetryCountdown={autoRetryCountdown}
        maxRetries={maxRetries}
        onRetry={resetAndRetry}
      />

      {isLoading ? (
        <Spinner label={t('app.loading')} style={{ margin: '32px auto' }} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={styles.th}>Typ</TableHeaderCell>
              <TableHeaderCell className={styles.th}>Od</TableHeaderCell>
              <TableHeaderCell className={styles.th}>Do</TableHeaderCell>
              {!isMobile && <TableHeaderCell className={styles.th}>Dní / Hodin</TableHeaderCell>}
              {!isMobile && <TableHeaderCell className={styles.th}>Místo pobytu</TableHeaderCell>}
              <TableHeaderCell className={styles.th}>Stav</TableHeaderCell>
              {!isMobile && <TableHeaderCell className={styles.th}>Podáno</TableHeaderCell>}
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...(requests ?? [])].sort((a, b) => b.startDate.localeCompare(a.startDate)).map(r => {
              const hours = r.totalHours   // uložený snapshot (dle úvazku v době čerpání), ne dopočet z aktuálního úvazku
              const canEdit = r.status === AbsenceRequestStatus.Pending
              return (
                <TableRow key={r.id} className={styles.tableRow}>
                  <TableCell>
                    {r.absenceTypeName}
                    {r.workloadMismatch && (
                      <Tooltip relationship="label" content="Počet hodin nemusí odpovídat úvazku platnému v termínu této dovolené (úvazek se v roce měnil). Zkontrolujte prosím s HR.">
                        <span style={{ color: tokens.colorStatusWarningForeground1, marginLeft: 6, cursor: 'help', fontWeight: 700 }}>⚠</span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{new Date(r.startDate).toLocaleDateString('cs-CZ')}</TableCell>
                  <TableCell>{new Date(r.endDate).toLocaleDateString('cs-CZ')}</TableCell>
                  {!isMobile && (
                    <TableCell>
                      {String(r.totalDays).replace('.', ',')} dní{r.dayHalf !== DayHalf.Full ? ` (${DAY_HALF_LABEL[r.dayHalf]})` : ''}
                      <span style={{ color: tokens.colorNeutralForeground3, fontSize: '12px', marginLeft: 4 }}>
                        / {String(hours).replace('.', ',')} h
                      </span>
                    </TableCell>
                  )}
                  {!isMobile && <TableCell>{r.location ?? '–'}</TableCell>}
                  <TableCell>
                    <Badge color={STATUS_COLOR[r.status] ?? 'subtle'}>
                      {STATUS_LABEL[r.status] ?? r.status}
                    </Badge>
                  </TableCell>
                  {!isMobile && <TableCell>{new Date(r.requestedAt).toLocaleDateString('cs-CZ')}</TableCell>}
                  <TableCell onClick={e => e.stopPropagation()}>
                    {canEdit && (
                      <div className={styles.actions}>
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Edit24Regular />}
                          title="Upravit"
                          onClick={() => openEditDialog(r)}
                        />
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Delete24Regular />}
                          title="Stornovat"
                          disabled={cancelMutation.isPending}
                          onClick={() => handleCancel(r)}
                        />
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onOpenChange={(_, d) => { if (!d.open) resetDialog() }}>
        <DialogSurface style={{ width: '680px', maxWidth: '95vw' }}>
          <DialogTitle>{editingRequest ? 'Upravit žádost' : t('absences.newRequest')}</DialogTitle>
          <DialogBody>
            <DialogContent>
              <div className={styles.formGrid}>
                <div className={styles.fullRow}>
                  <VacationBalanceSummary compact />
                </div>

                {formError && (
                  <div className={styles.fullRow}>
                    <MessageBar intent="error"><MessageBarBody>{formError}</MessageBarBody></MessageBar>
                  </div>
                )}

                <div className={styles.fullRow}>
                  <Field label="Typ nepřítomnosti" required>
                    <Select value={form.absenceTypeId} onChange={set('absenceTypeId')}>
                      <option value="">– vyberte –</option>
                      {absenceTypes?.map(at => (
                        <option key={at.id} value={at.id}>{at.name}</option>
                      ))}
                    </Select>
                  </Field>
                  {absenceTypes?.find(at => String(at.id) === form.absenceTypeId)?.code === 'PAID_SPECIAL' && (
                    <div style={{ marginTop: '4px', fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                      Placené volno (svatba, pohřeb, narození dítěte, doktor, doprovod, darování krve a krevní plazmy…)
                    </div>
                  )}
                </div>

                <Field label="Datum od" required>
                  <NativeDateInput value={form.startDate} onChange={v => { setForm(f => ({ ...f, startDate: v })); setTotalDaysEdited(false) }} />
                </Field>

                <Field label="Datum do" required>
                  <NativeDateInput
                    value={form.endDate}
                    onChange={v => { setForm(f => ({ ...f, endDate: v })); setTotalDaysEdited(false) }}
                    min={form.startDate || undefined}
                  />
                </Field>

                {isSingleDay ? (
                  <Field label="Rozsah" required>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button appearance={form.dayHalf === DayHalf.Full ? 'primary' : 'secondary'} onClick={setFullDay}>Celý den</Button>
                      <Button appearance={form.dayHalf === DayHalf.Morning ? 'primary' : 'secondary'} onClick={() => setHalf(DayHalf.Morning)}>Dopoledne</Button>
                      <Button appearance={form.dayHalf === DayHalf.Afternoon ? 'primary' : 'secondary'} onClick={() => setHalf(DayHalf.Afternoon)}>Odpoledne</Button>
                    </div>
                  </Field>
                ) : (
                  <Field label="Počet pracovních dnů" required>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Button size="small" onClick={() => adjustWholeDays(-1)} disabled={(parseDecimal(form.totalDays) || 0) <= 1}>−</Button>
                      <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>{form.totalDays || '—'}</span>
                      <Button size="small" onClick={() => adjustWholeDays(1)}>+</Button>
                    </div>
                    <div className={styles.hint}>
                      {totalDaysEdited ? 'Ručně upraveno' : 'Automaticky vypočteno (bez svátků a víkendů)'}
                    </div>
                  </Field>
                )}

                <Field label="Místo pobytu" required className={styles.fullRow}>
                  <Input value={form.location} onChange={set('location')} />
                </Field>

                <Field label="Poznámka" className={styles.fullRow}>
                  <Textarea value={form.note} onChange={set('note')} rows={2} />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={resetDialog}>
                {t('common.cancel')}
              </Button>
              <Button appearance="primary" disabled={isPending} onClick={handleSubmit}>
                {isPending ? 'Ukládám...' : editingRequest ? 'Uložit změny' : t('absences.actions.submit')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
